import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "./services/api";
import { picturesApi } from "./pictures";
import {
  calendarApi,
  familiesApi,
  feedApi,
  holidaysApi,
  imprintsApi,
  memoriesApi,
  nicknamesApi,
  profilesApi,
  photosApi,
  wishlistApi,
  thankYousApi,
  usersApi,
  type CalendarEvent,
  type Family,
  type Imprint,
  type Memory,
  type Profile,
  type ProfileCreate,
  type WishlistItem,
  type FamilyMember,
} from "./services/kindlyApi";

// One key per kind of server data, so a write names what it changed.
export const queryKeys = {
  nicknames: (userId: number) => ["nicknames", userId] as const,
  thankYous: ["thank-yous"] as const,
  adminVerify: ["admin", "verify"] as const,
  calendar: ["calendar"] as const,
  profiles: ["profiles"] as const,
  profile: (id: number) => ["profile", id] as const,
  ownProfile: (email: string) => ["own-profile", email] as const,
  wishlist: ["wishlist"] as const,
  claimedGifts: (profileId: number) => ["claimed-gifts", profileId] as const,
  memories: ["memories"] as const,
  imprints: ["imprints"] as const,
  feed: (excludeUserId: number) => ["feed", excludeUserId] as const,
  families: ["families"] as const,
  familyUsers: ["family-users"] as const,
  photoUrl: (s3Key: string) => ["photo-url", s3Key] as const,
  pictures: ["pictures"] as const,
};

// ---- nicknames, per profile

/** The nicknames others have given profile `userId`; asks nothing without one. */
export const useNicknames = (userId: number | null) =>
  useQuery({
    queryKey: queryKeys.nicknames(userId ?? 0),
    queryFn: () => nicknamesApi.getForUser(userId ?? 0),
    enabled: userId !== null,
  });

export function useCreateNickname() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (nickname: {
      userId: number;
      nickname: string;
      createdBy: number;
      story?: string;
    }) =>
      nicknamesApi.create(
        nickname.userId,
        nickname.nickname,
        nickname.createdBy,
        nickname.story,
      ),
    onSuccess: (_created, { userId }) =>
      client.invalidateQueries({ queryKey: queryKeys.nicknames(userId) }),
  });
}

export function useUpdateNickname() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (nickname: {
      nicknameId: number;
      nickname: string;
      story: string;
      updatedBy: number;
      userId: number;
    }) =>
      nicknamesApi.update(
        nickname.nicknameId,
        nickname.nickname,
        nickname.updatedBy,
        nickname.story,
      ),
    onSuccess: (_updated, { userId }) =>
      client.invalidateQueries({ queryKey: queryKeys.nicknames(userId) }),
  });
}

export function useDeleteNickname() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (nickname: {
      nicknameId: number;
      deletedBy: number;
      userId: number;
    }) => nicknamesApi.delete(nickname.nicknameId, nickname.deletedBy),
    onSuccess: (_deleted, { userId }) =>
      client.invalidateQueries({ queryKey: queryKeys.nicknames(userId) }),
  });
}

// ---- thank-you notes

export const useThankYous = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.thankYous,
    queryFn: () => thankYousApi.getAll(),
    enabled,
  });

export function useCreateThankYou() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (note: {
      thankYouData: {
        to_user_id: number;
        gift_description: string;
        message: string;
      };
      currentUserId: number;
    }) => thankYousApi.create(note.thankYouData, note.currentUserId),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: queryKeys.thankYous }),
  });
}

export function useDeleteThankYou() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => thankYousApi.delete(id),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: queryKeys.thankYous }),
  });
}

// ---- photos

/** Presigned URLs last an hour: reuse one for fifty minutes. */
const PHOTO_URL_FRESH_MS = 50 * 60 * 1000;

const photoUrlQuery = (s3Key: string) => ({
  queryKey: queryKeys.photoUrl(s3Key),
  queryFn: async () => (await photosApi.getS3Url(s3Key)).url,
  staleTime: PHOTO_URL_FRESH_MS,
  gcTime: PHOTO_URL_FRESH_MS,
});

/** The display URL of one stored photo, or null while it loads (or if it can't be read). */
export function usePhotoUrl(s3Key: string | null | undefined): string | null {
  const { data } = useQuery({
    ...photoUrlQuery(s3Key ?? ""),
    enabled: !!s3Key,
  });
  return data ?? null;
}

/** The display URLs of stored photos, by key; a key shows up once its URL has loaded. */
export function usePhotoUrls(s3Keys: string[]): Record<string, string> {
  const unique = Array.from(new Set(s3Keys));
  return useQueries({
    queries: unique.map(photoUrlQuery),
    combine: (results) =>
      Object.fromEntries(
        results.flatMap((result, i) =>
          result.data ? [[unique[i], result.data]] : [],
        ),
      ),
  });
}

export const useUploadPhoto = () =>
  useMutation({
    mutationFn: (upload: {
      file: File;
      folder?: "memories" | "profiles" | "imprints";
    }) => photosApi.upload(upload.file, upload.folder),
  });

// ---- the family's profile picture pool

/** The signed-in person's pool; asks nothing until `enabled` (the picker is open). */
export const usePictures = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.pictures,
    queryFn: () => picturesApi.list(),
    enabled,
  });

/** Adds a picture to the pool (the picker then selects it). */
export function useUploadPicture() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => picturesApi.upload(file),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.pictures }),
  });
}

/** Removes a picture from the pool. */
export function useDeletePicture() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => picturesApi.remove(id),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.pictures }),
  });
}

// ---- families

export interface FamiliesWithMembers {
  families: Family[];
  /** familyId -> members */
  members: Record<number, FamilyMember[]>;
}

/** The caller's families and their members, read together. */
export const useFamilies = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.families,
    queryFn: async (): Promise<FamiliesWithMembers> => {
      const list = await familiesApi.getAll();
      const families = Array.isArray(list) ? list : [];
      const lists = await Promise.all(
        families.map((family) => familiesApi.getMembers(family.id)),
      );
      return {
        families,
        members: Object.fromEntries(
          families.map((family, i) => [
            family.id,
            Array.isArray(lists[i]) ? lists[i] : [],
          ]),
        ),
      };
    },
    enabled,
  });

/** Reloads the families, after anything that changes who is in them. */
export function useFamiliesChanged() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.families });
}

export function useCreateFamily() {
  const changed = useFamiliesChanged();
  return useMutation({
    mutationFn: (family: { name: string; description?: string }) =>
      familiesApi.create(family),
    onSuccess: changed,
  });
}

export function useUpdateFamily() {
  const changed = useFamiliesChanged();
  return useMutation({
    mutationFn: (update: {
      id: number;
      data: { name?: string; description?: string };
    }) => familiesApi.update(update.id, update.data),
    onSuccess: changed,
  });
}

// ---- the people in the caller's families

/** Everyone in the caller's families, for the pickers that tag or invite people. */
export const useFamilyUsers = () =>
  useQuery({
    queryKey: queryKeys.familyUsers,
    queryFn: async () => {
      const users = await usersApi.getAllForFamilies();
      return Array.isArray(users) ? users : [];
    },
  });

// ---- admin

/**
 * Asks the server whether the signed-in user is an admin (the `admin` Cognito
 * group), once per sign-in. A user who is not loses any stored override.
 */
export const useAdminVerification = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.adminVerify,
    queryFn: async () => {
      const res = await apiClient.get<{ is_admin: boolean }>("/admin/verify");
      // Only the answer: useAdmin ANDs it with the override, so a non-admin
      // never sees admin UI. Switching the stored override off here would
      // hide the Admin link from the next admin on this device for good.
      return res.is_admin === true;
    },
    enabled,
    staleTime: Infinity,
  });

// ---- the calendar

export const useCalendarEvents = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.calendar,
    queryFn: () => calendarApi.getAll(),
    enabled,
  });

function useCalendarChanged() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.calendar });
}

export function useCreateCalendarEvent() {
  const changed = useCalendarChanged();
  return useMutation({
    mutationFn: (event: Omit<CalendarEvent, "id">) => calendarApi.create(event),
    onSuccess: changed,
  });
}

export function useUpdateCalendarEvent() {
  const changed = useCalendarChanged();
  return useMutation({
    mutationFn: (update: { id: number; data: Partial<CalendarEvent> }) =>
      calendarApi.update(update.id, update.data),
    onSuccess: changed,
  });
}

export function useDeleteCalendarEvent() {
  const changed = useCalendarChanged();
  return useMutation({
    mutationFn: (id: number) => calendarApi.delete(id),
    onSuccess: changed,
  });
}

export function useGenerateHolidays() {
  const changed = useCalendarChanged();
  return useMutation({
    mutationFn: (year: number) => holidaysApi.generateForYear(year),
    onSuccess: changed,
  });
}

// ---- the feed

/** The newest activity of the caller's families, not counting `excludeUserId`'s own. */
export const useFeed = (excludeUserId: number, limit = 50) =>
  useQuery({
    queryKey: queryKeys.feed(excludeUserId),
    queryFn: () => feedApi.getAll(limit, excludeUserId),
    select: (activities) =>
      (Array.isArray(activities) ? activities : [])
        .map((item) => ({ ...item }))
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        ),
  });

// ---- memories

export const useMemories = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.memories,
    queryFn: () => memoriesApi.getAll(),
    enabled,
  });

/** Reloads the memories, after something changed them (or their photos). */
export function useMemoriesChanged() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.memories });
}

export function useCreateMemory() {
  const changed = useMemoriesChanged();
  return useMutation({
    mutationFn: (memory: Omit<Memory, "id" | "created_at" | "comments">) =>
      memoriesApi.create(memory),
    onSuccess: changed,
  });
}

export function useUpdateMemory() {
  const changed = useMemoriesChanged();
  return useMutation({
    mutationFn: (update: { id: number; data: Partial<Memory> }) =>
      memoriesApi.update(update.id, update.data),
    onSuccess: changed,
  });
}

export function useDeleteMemory() {
  const changed = useMemoriesChanged();
  return useMutation({
    mutationFn: (id: number) => memoriesApi.delete(id),
    onSuccess: changed,
  });
}

export function useAddMemoryComment() {
  const changed = useMemoriesChanged();
  return useMutation({
    mutationFn: (comment: {
      memoryId: number;
      comment: string;
      userId: number;
      userName: string;
    }) =>
      memoriesApi.addComment(comment.memoryId, {
        comment: comment.comment,
        user_id: comment.userId,
        user_name: comment.userName,
      }),
    onSuccess: changed,
  });
}

// ---- imprints

export const useImprints = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.imprints,
    queryFn: () => imprintsApi.getAll(),
    enabled,
  });

/** Reloads the imprints, after something changed them (or their photos). */
export function useImprintsChanged() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.imprints });
}

export function useCreateImprint() {
  const changed = useImprintsChanged();
  return useMutation({
    mutationFn: (imprint: Omit<Imprint, "id" | "created_at" | "comments">) =>
      imprintsApi.create(imprint),
    onSuccess: changed,
  });
}

export function useUpdateImprint() {
  const changed = useImprintsChanged();
  return useMutation({
    mutationFn: (update: { id: number; data: Partial<Imprint> }) =>
      imprintsApi.update(update.id, update.data),
    onSuccess: changed,
  });
}

export function useDeleteImprint() {
  const changed = useImprintsChanged();
  return useMutation({
    mutationFn: (id: number) => imprintsApi.delete(id),
    onSuccess: changed,
  });
}

export function useAddImprintComment() {
  const changed = useImprintsChanged();
  return useMutation({
    mutationFn: (comment: {
      imprintId: number;
      comment: string;
      userId: number;
      userName: string;
    }) =>
      imprintsApi.addComment(comment.imprintId, {
        comment: comment.comment,
        user_id: comment.userId,
        user_name: comment.userName,
      }),
    onSuccess: changed,
  });
}

// ---- the wishlist

/** Every wish in the caller's families, and the people they are for. */
export const useWishlist = () =>
  useQuery({
    queryKey: queryKeys.wishlist,
    queryFn: () => wishlistApi.getAll(),
    select: (data) => ({
      items: data.wishlist_items ?? [],
      wishers: Array.isArray(data.wishers) ? data.wishers : [],
    }),
  });

/** The gifts profile `profileId` has been given. */
export const useClaimedGifts = (profileId: number | null) =>
  useQuery({
    queryKey: queryKeys.claimedGifts(profileId ?? 0),
    queryFn: () => wishlistApi.getClaimedGifts(profileId ?? 0),
    enabled: profileId !== null,
  });

function useWishlistChanged() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.wishlist });
}

export function useCreateWish() {
  const changed = useWishlistChanged();
  return useMutation({
    mutationFn: (
      item: Omit<WishlistItem, "id" | "owner" | "claim_status" | "claimed_by">,
    ) => wishlistApi.create(item),
    onSuccess: changed,
  });
}

export function useUpdateWish() {
  const changed = useWishlistChanged();
  return useMutation({
    mutationFn: (update: { id: number; data: Partial<WishlistItem> }) =>
      wishlistApi.update(update.id, update.data),
    onSuccess: changed,
  });
}

export function useDeleteWish() {
  const changed = useWishlistChanged();
  return useMutation({
    mutationFn: (id: number) => wishlistApi.delete(id),
    onSuccess: changed,
  });
}

export function useClaimWish() {
  const changed = useWishlistChanged();
  return useMutation({
    mutationFn: (claim: {
      itemId: number;
      claimedBy: string;
      revealDate?: string;
    }) => wishlistApi.claim(claim.itemId, claim.claimedBy, claim.revealDate),
    onSuccess: changed,
  });
}

export function useUnclaimWish() {
  const changed = useWishlistChanged();
  return useMutation({
    mutationFn: (itemId: number) => wishlistApi.unclaim(itemId),
    onSuccess: changed,
  });
}

export function useMoveWish() {
  const changed = useWishlistChanged();
  return useMutation({
    mutationFn: (move: { itemId: number; newWisheeId: number }) =>
      wishlistApi.move(move.itemId, move.newWisheeId),
    onSuccess: changed,
  });
}

// ---- profiles

/** A loaded profile list is reused this long before a page's mount reads it again. */
const PROFILES_FRESH_MS = 60_000;

/** Stands in for the list until it loads, so effects and memos see one array. */
export const NO_PROFILES: Profile[] = [];

/** Everyone the caller can see; the dropdowns, mentions and pickers share one read. */
export const useProfiles = () =>
  useQuery({
    queryKey: queryKeys.profiles,
    queryFn: async () => {
      const profiles = await profilesApi.getAll();
      return Array.isArray(profiles) ? profiles : [];
    },
    staleTime: PROFILES_FRESH_MS,
  });

/**
 * One person's profile, for the Profile page. Their copy in the list shows
 * at once while the full profile is read.
 */
export function useProfile(id: number | null) {
  const client = useQueryClient();
  return useQuery({
    queryKey: queryKeys.profile(id ?? 0),
    queryFn: () => profilesApi.getById(id ?? 0),
    enabled: id !== null,
    placeholderData: () =>
      client
        .getQueryData<Profile[]>(queryKeys.profiles)
        ?.find((profile) => profile.id === id),
  });
}

/**
 * The signed-in user's own profile, looked up by email. A miss (no profile
 * yet) is an error here, not a page error: the Profile page creates one.
 */
export function useOwnProfile(email: string | null, enabled: boolean) {
  const client = useQueryClient();
  return useQuery({
    queryKey: queryKeys.ownProfile(email ?? ""),
    queryFn: async () => {
      const profile = await profilesApi.getByEmail(email ?? "");
      // So the Profile page already has it when it asks for that id.
      client.setQueryData(queryKeys.profile(profile.id), profile);
      return profile;
    },
    enabled: enabled && !!email,
    retry: false,
  });
}

export function useCreateProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (profile: ProfileCreate) => profilesApi.create(profile),
    onSuccess: (created) => {
      client.setQueryData(queryKeys.profile(created.id), created);
      return client.invalidateQueries({ queryKey: queryKeys.profiles });
    },
  });
}

export function useUpdateProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (update: { id: number; data: Partial<Profile> }) =>
      profilesApi.update(update.id, update.data),
    onSuccess: (updated) => {
      client.setQueryData(queryKeys.profile(updated.id), updated);
      client.setQueriesData<Profile>({ queryKey: ["own-profile"] }, (own) =>
        own?.id === updated.id ? updated : own,
      );
      return client.invalidateQueries({ queryKey: queryKeys.profiles });
    },
  });
}
